import { useTranslation } from 'react-i18next';
import { cn, FormatUtils, PaletteUtils } from '@shared/utils';
import type { ProfileResponse } from '@shared/api/rest/profile/models';
import type { UserResponse } from '@shared/api/rest/users';
import { ColorsEnum, Icon, Text } from '@shared/widgets';
import LockIcon from '@assets/icons/lock.svg';
import UserIcon from '@assets/icons/user2.svg';
import ReportIcon from '@assets/icons/report.svg';
import MailIcon from '@assets/icons/mail.svg';
import PhoneIcon from '@assets/icons/phone.svg';

interface IUserInfoHeaderProps {
  className?: string;
  user: ProfileResponse | UserResponse;
}

export const UserInfoHeader = (props: IUserInfoHeaderProps) => {
  const { t } = useTranslation();

  return (
    <div className={cn(props.className)}>
      <div
        className={cn(
          'flex items-center bg-white p-4 dark:bg-soft-black',
          'max-[1400px]:flex-col',
          PaletteUtils.isVcfcPalette && 'rounded-xl dark:border dark:border-gray-black',
        )}
      >
        <div className={cn('w-2/4 self-start p-4', 'max-[1400px]:w-full')}>
          <div className='shrink-1 flex items-center'>
            <div className='relative mr-8 flex h-[104px] w-[104px] flex-none select-none items-center justify-center overflow-hidden rounded-full text-center'>
              <div className='m-win-0 relative z-10 flex h-full w-full items-center justify-center rounded-[inherit] bg-primary'>
                <Text weight='medium' size='xl' color={PaletteUtils.getColor(ColorsEnum.Black, ColorsEnum.White)}>
                  {props.user?.firstName?.split('')?.[0]}
                </Text>
              </div>
              {props.user.locked && (
                <div className='absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center rounded-[inherit] bg-dark-wet-asphalt'>
                  <Icon icon={<LockIcon />} color='red' />
                </div>
              )}
            </div>
            <div className={cn(props.user.locked && 'opacity-50')}>
              {(props.user?.firstName || props.user?.lastName) && (
                <Text className='overflow-hidden text-ellipsis' weight='medium' size='2xl'>
                  {props.user.firstName} {props.user.lastName}
                </Text>
              )}
              {props.user?.userType && (
                <Text className='mt-1 opacity-60' weight='medium'>
                  {t(`role.${props.user.userType.toLowerCase()}`)}
                </Text>
              )}
            </div>
          </div>
        </div>
        <div className={cn('w-1/2 p-4', 'max-[1400px]:w-full', props.user.locked && 'opacity-50')}>
          <ul className='text-sm font-medium'>
            {props.user?.userName && (
              <li className='mb-2 flex items-center gap-2'>
                <Icon icon={<UserIcon />} color='primary' />
                {props.user.userName}
              </li>
            )}
            {props.user?.position && (
              <li className='mb-2 flex items-center gap-2'>
                <Icon icon={<ReportIcon width='18' height='18' />} color='primary' />
                {props.user.position}
              </li>
            )}
            {props.user?.emailAddress && (
              <li className='mb-2 flex items-center gap-2'>
                <Icon icon={<MailIcon />} color='primary' />
                {props.user.emailAddress}
              </li>
            )}
            {props.user.phoneAreaCode && props.user.phoneNumber && (
              <li className='flex items-center gap-2'>
                <Icon icon={<PhoneIcon />} color='primary' />
                {FormatUtils.toPhoneFormat(props.user.phoneAreaCode, props.user.phoneNumber)}
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};
